import React, { useEffect, useState } from "react";
import DesktopLayout from "@/components/Layout/DesktopLayout";
import { api, formatApiError } from "@/api/client";
import { ROLES } from "@/lib/workflow";
import { Plus, Trash2, Loader2, KeyRound, ShieldCheck, ChevronDown, ChevronUp, Save, Pencil, X } from "lucide-react";
import { toast } from "sonner";

const FIELD = "w-full bg-white border border-slate-300 rounded-sm p-2.5 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none text-sm";

const PERMISSION_KEYS = [
  { key: "create_customers",         label: "Create / edit customers" },
  { key: "create_projects",          label: "Create new projects" },
  { key: "create_quotes",            label: "Create quotes" },
  { key: "convert_quotes",           label: "Convert quote to project (Order Confirmed)" },
  { key: "access_general_settings",  label: "Access — General settings" },
  { key: "access_project_settings",  label: "Access — Project settings" },
  { key: "access_quote_settings",    label: "Access — Quote settings" },
  { key: "access_dealer_settings",   label: "Access — Dealer settings" },
  { key: "access_users",             label: "Access — Users admin" },
  { key: "access_costing",           label: "Access — Costing (Margin + Purchase Board)" },
  { key: "access_sales_receivables", label: "Access — Sales (Pending Receivables)" },
];

export default function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "sales", phone: "", password: "", assigned_sales_id: "" });
  const [expanded, setExpanded] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/users"); setUsers(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const salesUsers = users.filter((u) => u.role === "sales");

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      // Only send assigned_sales_id when role is dealer
      if (payload.role !== "dealer") delete payload.assigned_sales_id;
      else if (!payload.assigned_sales_id) delete payload.assigned_sales_id;
      await api.post("/users", payload);
      toast.success("User created");
      setForm({ email: "", name: "", role: "sales", phone: "", password: "", assigned_sales_id: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const resetPassword = async (id) => {
    const pw = prompt("New password (min 6 chars):", "hk-demo-2024");
    if (!pw || pw.length < 6) return;
    try { await api.patch(`/users/${id}`, { password: pw }); toast.success("Password updated"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DesktopLayout>
      <div className="mb-6">
        <div className="hk-tiny-label">Administration</div>
        <h1 className="text-4xl font-black tracking-tighter" style={{ fontFamily: "Chivo" }}>Users &amp; roles</h1>
        <p className="text-slate-500 mt-2 text-sm">Manage who can log in, their team, and per-user permissions on top of their role.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={create} className="hk-card p-6 space-y-3 lg:col-span-1 h-fit" data-testid="user-create-form">
          <h3 className="font-bold mb-1" style={{ fontFamily: "Chivo" }}>Add user</h3>
          <div>
            <label className="hk-tiny-label block mb-1.5">Email</label>
            <input className={FIELD} required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-create-email" />
          </div>
          <div>
            <label className="hk-tiny-label block mb-1.5">Full name</label>
            <input className={FIELD} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-create-name" />
          </div>
          <div>
            <label className="hk-tiny-label block mb-1.5">Role</label>
            <select className={FIELD} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-create-role">
              {Object.entries(ROLES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          {form.role === "dealer" && (
            <div>
              <label className="hk-tiny-label block mb-1.5">Assigned sales person</label>
              <select
                className={FIELD}
                value={form.assigned_sales_id}
                onChange={(e) => setForm({ ...form, assigned_sales_id: e.target.value })}
                data-testid="user-create-assigned-sales"
              >
                <option value="">— Unassigned —</option>
                {salesUsers.map((s) => (
                  <option key={s.id} value={s.id}>{s.name} ({s.email})</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">The selected sales person will see this dealer&apos;s quotes alongside their own.</p>
            </div>
          )}
          <div>
            <label className="hk-tiny-label block mb-1.5">Phone</label>
            <input className={FIELD} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div>
            <label className="hk-tiny-label block mb-1.5">Password (leave blank for default)</label>
            <input className={FIELD} type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="hk-demo-2024" />
          </div>
          <button disabled={busy} className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-sm py-2.5 inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="user-create-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create user
          </button>
        </form>

        <div className="lg:col-span-2 hk-card overflow-hidden">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="bg-slate-50 hk-tiny-label">
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Role</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const meta = ROLES[u.role] || {};
                const isOpen = expanded === u.id;
                return (
                  <React.Fragment key={u.id}>
                    <tr className="border-t border-slate-100">
                      <td className="px-4 py-3 font-semibold">{u.name}</td>
                      <td className="px-4 py-3 text-slate-600">{u.email}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full" style={{ background: meta.color }} />
                          {meta.label || u.role}
                        </span>
                        {u.role === "dealer" && u.assigned_sales_name && (
                          <div className="text-[11px] text-slate-500 mt-0.5" data-testid={`assigned-sales-label-${u.email}`}>
                            ↳ Sales: <span className="font-semibold text-slate-700">{u.assigned_sales_name}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          onClick={() => setEditing(u)}
                          className="text-slate-500 hover:text-orange-600 mr-3"
                          title="Edit user"
                          data-testid={`user-edit-${u.email}`}
                        >
                          <Pencil className="w-4 h-4 inline" />
                        </button>
                        <button
                          onClick={() => setExpanded(isOpen ? null : u.id)}
                          className="text-slate-500 hover:text-orange-600 mr-3"
                          title="Permissions"
                          data-testid={`user-perms-${u.email}`}
                        >
                          <ShieldCheck className="w-4 h-4 inline" /> {isOpen ? <ChevronUp className="w-3 h-3 inline" /> : <ChevronDown className="w-3 h-3 inline" />}
                        </button>
                        <button onClick={() => resetPassword(u.id)} className="text-slate-500 hover:text-orange-600 mr-3" title="Reset password" data-testid={`user-reset-${u.email}`}>
                          <KeyRound className="w-4 h-4 inline" />
                        </button>
                        <button onClick={() => remove(u.id)} className="text-slate-500 hover:text-red-600" title="Delete" data-testid={`user-delete-${u.email}`}>
                          <Trash2 className="w-4 h-4 inline" />
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-slate-50/60 border-t border-slate-100" data-testid={`user-perms-panel-${u.email}`}>
                        <td colSpan={4} className="px-4 py-4">
                          <PermissionsEditor user={u} onSaved={load} salesUsers={salesUsers} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {editing && (
        <EditUserModal user={editing} salesUsers={salesUsers}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
    </DesktopLayout>
  );
}

function EditUserModal({ user, salesUsers, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    role: user.role || "sales",
    phone: user.phone || "",
    assigned_sales_id: user.assigned_sales_id || "",
  });
  const [busy, setBusy] = useState(false);
  const isSuperAdmin = user.role === "super_admin";
  const dirty =
    form.name !== (user.name || "")
    || form.email !== (user.email || "")
    || form.role !== (user.role || "sales")
    || form.phone !== (user.phone || "")
    || (form.role === "dealer" && form.assigned_sales_id !== (user.assigned_sales_id || ""));
  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required"); return;
    }
    setBusy(true);
    try {
      const payload = {};
      if (form.name !== user.name) payload.name = form.name.trim();
      if (form.email.toLowerCase() !== (user.email || "").toLowerCase()) payload.email = form.email.trim();
      // Role changes are blocked for Super Admin to prevent lock-out
      if (!isSuperAdmin && form.role !== user.role) payload.role = form.role;
      if (form.phone !== (user.phone || "")) payload.phone = form.phone;
      // Only send assigned_sales_id when the (new) role is dealer
      const finalRole = payload.role || user.role;
      if (finalRole === "dealer" && form.assigned_sales_id !== (user.assigned_sales_id || "")) {
        payload.assigned_sales_id = form.assigned_sales_id;
      }
      await api.patch(`/users/${user.id}`, payload);
      toast.success("User updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}
      data-testid="user-edit-modal">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div>
            <div className="hk-tiny-label" style={{ color: "#E52B50" }}>Edit User</div>
            <div className="text-sm font-black text-slate-900 mt-0.5 truncate max-w-[320px]">{user.name}</div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800" data-testid="user-edit-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="hk-tiny-label block mb-1.5">Full name</label>
            <input className={FIELD} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="user-edit-name" />
          </div>
          <div>
            <label className="hk-tiny-label block mb-1.5">Email</label>
            <input className={FIELD} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="user-edit-email" />
          </div>
          <div>
            <label className="hk-tiny-label block mb-1.5">Role</label>
            <select className={FIELD} value={form.role} disabled={isSuperAdmin}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              data-testid="user-edit-role">
              {Object.entries(ROLES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            {isSuperAdmin && (
              <p className="text-[11px] text-slate-500 mt-1">Super Admin role is locked to prevent accidental lock-out.</p>
            )}
          </div>
          {form.role === "dealer" && (
            <div>
              <label className="hk-tiny-label block mb-1.5">Assigned sales person</label>
              <select className={FIELD} value={form.assigned_sales_id}
                onChange={(e) => setForm({ ...form, assigned_sales_id: e.target.value })}
                data-testid="user-edit-assigned-sales">
                <option value="">— Unassigned —</option>
                {salesUsers.map((s) => (
                  <option key={s.id} value={s.id}>{s.name} ({s.email})</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="hk-tiny-label block mb-1.5">Phone</label>
            <input className={FIELD} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="user-edit-phone" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose}
            className="border border-slate-300 hover:bg-white text-slate-700 text-xs font-black uppercase px-3 py-1.5 rounded-sm"
            data-testid="user-edit-cancel">Cancel</button>
          <button disabled={!dirty || busy} onClick={save}
            className="bg-slate-900 disabled:opacity-40 hover:bg-slate-800 text-white text-xs font-black uppercase px-3 py-1.5 rounded-sm inline-flex items-center gap-1"
            data-testid="user-edit-save">
            {busy && <Loader2 className="w-3 h-3 animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function PermissionsEditor({ user, onSaved, salesUsers = [] }) {
  const [perms, setPerms] = useState(() => {
    const initial = {};
    for (const p of PERMISSION_KEYS) initial[p.key] = !!(user.permissions || {})[p.key];
    return initial;
  });
  const [assignedSales, setAssignedSales] = useState(user.assigned_sales_id || "");
  const [busy, setBusy] = useState(false);

  const permsDirty = PERMISSION_KEYS.some((p) => perms[p.key] !== !!(user.permissions || {})[p.key]);
  const assignmentDirty = user.role === "dealer" && (assignedSales || "") !== (user.assigned_sales_id || "");
  const dirty = permsDirty || assignmentDirty;

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      if (permsDirty) payload.permissions = perms;
      if (assignmentDirty) payload.assigned_sales_id = assignedSales || "";
      await api.patch(`/users/${user.id}`, payload);
      toast.success(`Saved ${user.name}`);
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const isSuperAdmin = user.role === "super_admin";
  const isDealer = user.role === "dealer";

  return (
    <div>
      {isDealer && (
        <div className="mb-4">
          <div className="hk-tiny-label mb-1.5">Assigned sales person</div>
          <select
            className="bg-white border border-slate-300 rounded-sm p-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none min-w-[260px]"
            value={assignedSales}
            onChange={(e) => setAssignedSales(e.target.value)}
            data-testid={`assigned-sales-${user.email}`}
          >
            <option value="">— Unassigned —</option>
            {salesUsers.map((s) => (
              <option key={s.id} value={s.id}>{s.name} ({s.email})</option>
            ))}
          </select>
          {user.assigned_sales_name && (
            <p className="text-xs text-slate-500 mt-1">Currently: <span className="font-semibold">{user.assigned_sales_name}</span></p>
          )}
        </div>
      )}
      <div className="hk-tiny-label mb-2 flex items-center gap-2">
        <ShieldCheck className="w-3 h-3" />
        Per-user permission overrides
        {isSuperAdmin && <span className="ml-2 text-orange-600 italic">(Super Admin always has everything)</span>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 mb-3">
        {PERMISSION_KEYS.map((p) => (
          <label key={p.key} className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={!!perms[p.key]}
              disabled={isSuperAdmin}
              onChange={(e) => setPerms({ ...perms, [p.key]: e.target.checked })}
              data-testid={`perm-${user.email}-${p.key}`}
              className="accent-orange-500"
            />
            <span className={isSuperAdmin ? "text-slate-400" : ""}>{p.label}</span>
          </label>
        ))}
      </div>
      <button
        onClick={save}
        disabled={!dirty || busy || isSuperAdmin}
        className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-4 py-2 rounded-sm text-sm inline-flex items-center gap-2 disabled:opacity-40"
        data-testid={`perm-save-${user.email}`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Save
      </button>
    </div>
  );
}
